import { Box } from "@tokenterminal/ui/Box"
import { HeadingWithCapsize as Heading } from "@tokenterminal/ui/Heading"
import { MiniChart } from "@tokenterminal/ui/MiniChart/MiniChart"
import { mediumChartCss, smallChartCss } from "./DataSnippet.css"
import { PlaceholderPlot } from "./PlaceholderPlot"
import { type Trend } from "./types"
import type { ReactNode } from "react"

export type { Trend }

const Plot = ({
  size,
  trend,
  values,
}: {
  size: "s" | "m"
  trend: Trend
  values?: number[]
}) => {
  return (
    <Box
      className={[
        {
          [mediumChartCss]: size === "m",
          [smallChartCss]: size === "s",
        },
      ]}
    >
      {values?.length ? (
        <MiniChart
          width="100%"
          height="100%"
          data={values}
          positive={trend === "positive"}
          negative={trend === "negative"}
          size={size}
        />
      ) : (
        <PlaceholderPlot />
      )}
    </Box>
  )
}

type Variant = "normal" | "small plot" | "no plot"

interface BaseProps {
  title: NonNullable<ReactNode>
  description: NonNullable<ReactNode>
  trend?: Trend
  change?: null | string // Change or value is required
  value?: null | string // Change or value is required
  plotValues?: number[]
}
interface ValueProps {
  value: null | string
}
interface ChangeProps {
  change: null | string
  trend: Trend
}

type Props = BaseProps & (ValueProps | ChangeProps)

const InnerSnippet = ({
  title,
  trend,
  value,
  change,
  description,
}: Omit<Props, "plotValues">) => {
  let textColorClass = "text-fg-secondary"
  if (trend === "positive") {
    textColorClass = "text-fg-emerald"
  } else if (trend === "negative") {
    textColorClass = "text-fg-red"
  }

  return (
    <div className="flex flex-col gap-1 max-w-full w-full justify-between">
      {typeof title === "string" ? (
        <Heading level={4} as="span">
          {title}
        </Heading>
      ) : (
        <div>{title}</div>
      )}

      {!value && !change ? (
        <div className="text-fg-secondary">N/A</div>
      ) : (
        <div className="flex gap-1 items-center justify-start text-xs">
          {value && <div className="text-fg-default">{value}</div>}
          {change && <div className={textColorClass}>{change}</div>}
        </div>
      )}
      {typeof description === "string" ? (
        <div className="text-fg-secondary text-xs">{description}</div>
      ) : (
        <div>{description}</div>
      )}
    </div>
  )
}

const NormalPlotSnippet = ({ plotValues, ...rest }: Props) => (
  <div className="flex gap-3 items-center justify-start">
    <Plot size="m" values={plotValues} trend={rest.trend} />
    <InnerSnippet {...rest} />
  </div>
)

type DataSnippetProps = Props & {
  variant?: Variant
}

export const DataSnippet = ({
  variant = "normal",
  ...props
}: DataSnippetProps) => {
  switch (variant) {
    case "small plot":
    case "no plot":
      return <InnerSnippet {...props} />
    case "normal":
    default:
      return <NormalPlotSnippet {...props} />
  }
}

DataSnippet.displayName = "DataSnippet"
